import { Button } from '@/components/ui/button';
import { Download, Upload } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import * as XLSX from 'xlsx';
import { BusinesscaseRevenue } from '@/types/businesscase';

interface ExcelImportExportProps {
  revenues: BusinesscaseRevenue[];
  onImport: (data: Partial<BusinesscaseRevenue>[]) => Promise<void>;
  onZymplerClick: () => void;
}

const COLUMN_MAPPING = {
  'Jaar': 'year',
  'Grid Connection': 'grid_connection_savings',
  'Grid Purchase': 'grid_purchase_cost_savings',
  'Dayahead': 'dayahead_incomes',
  "HBE's": 'hbe_incomes',
  'Energy Tax': 'energy_tax_savings',
  'Endex': 'endex_cost_savings',
  'Imbalance Income': 'imbalance_income',
  'Imbalance Discount': 'imbalance_discount',
  'EMS Costs (-)': 'ems_costs',
  'Total OPEX (-)': 'total_opex',
  'Generator': 'generator_cost_savings',
  'Extra SDE': 'extra_sde',
  'Total Solar': 'total_solar_revenues'
};

export function ExcelImportExport({ revenues, onImport, onZymplerClick }: ExcelImportExportProps) {
  const { toast } = useToast();

  const handleDownloadTemplate = () => {
    // Create worksheet data
    const wsData = [
      Object.keys(COLUMN_MAPPING), // Headers
      ...revenues.map(rev => [
        rev.year,
        rev.grid_connection_savings,
        rev.grid_purchase_cost_savings,
        rev.dayahead_incomes,
        rev.hbe_incomes,
        rev.energy_tax_savings,
        rev.endex_cost_savings,
        rev.imbalance_income,
        rev.imbalance_discount,
        rev.ems_costs,
        rev.total_opex,
        rev.generator_cost_savings,
        rev.extra_sde,
        rev.total_solar_revenues
      ])
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Opbrengsten');

    // Set column widths
    ws['!cols'] = Array(14).fill({ wch: 15 });

    XLSX.writeFile(wb, 'businesscase_opbrengsten_template.xlsx');

    toast({
      title: 'Template gedownload',
      description: 'Excel template is succesvol gedownload'
    });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json<any>(worksheet);

      // Validate and map data
      const mappedData: Partial<BusinesscaseRevenue>[] = jsonData.map((row, idx) => {
        // Initialize all fields to 0 to ensure complete overwrite
        const mapped: any = {
          grid_connection_savings: 0,
          grid_purchase_cost_savings: 0,
          dayahead_incomes: 0,
          hbe_incomes: 0,
          energy_tax_savings: 0,
          endex_cost_savings: 0,
          imbalance_income: 0,
          imbalance_discount: 0,
          ems_costs: 0,
          total_opex: 0,
          generator_cost_savings: 0,
          extra_sde: 0,
          total_solar_revenues: 0
        };
        
        // Year (required)
        const yearValue = row['Jaar'] || row['jaar'] || row['Year'];
        if (!yearValue) {
          throw new Error(`Geen jaar gevonden op rij ${idx + 2}`);
        }
        mapped.year = parseInt(yearValue);
        if (isNaN(mapped.year)) {
          throw new Error(`Ongeldige jaar waarde op rij ${idx + 2}`);
        }
        
        // Map all revenue fields with flexible header matching
        const fieldMappings = [
          { excel: ['Grid Connection'], db: 'grid_connection_savings' },
          { excel: ['Grid Purchase'], db: 'grid_purchase_cost_savings' },
          { excel: ['Dayahead'], db: 'dayahead_incomes' },
          { excel: ["HBE's", 'HBEs', 'HBE'], db: 'hbe_incomes' },
          { excel: ['Energy Tax'], db: 'energy_tax_savings' },
          { excel: ['Endex'], db: 'endex_cost_savings' },
          { excel: ['Imbalance Income'], db: 'imbalance_income' },
          { excel: ['Imbalance Discount'], db: 'imbalance_discount' },
          { excel: ['EMS Costs (-)', 'EMS Costs', 'EMS'], db: 'ems_costs' },
          { excel: ['Total OPEX (-)', 'Total OPEX', 'OPEX'], db: 'total_opex' },
          { excel: ['Generator'], db: 'generator_cost_savings' },
          { excel: ['Extra SDE'], db: 'extra_sde' },
          { excel: ['Total Solar Revenues', 'Total Solar', 'Totaal'], db: 'total_solar_revenues' }
        ];
        
        for (const { excel, db } of fieldMappings) {
          for (const excelHeader of excel) {
            const value = row[excelHeader];
            if (value !== undefined && value !== null && value !== '') {
              mapped[db] = parseFloat(value) || 0;
              break; // Stop after first match
            }
          }
        }

        // Find corresponding revenue by year
        const existingRevenue = revenues.find(r => r.year === mapped.year);
        if (existingRevenue) {
          mapped.id = existingRevenue.id;
          mapped.businesscase_id = existingRevenue.businesscase_id;
        }

        return mapped;
      });

      // Validate that we have data
      if (mappedData.length === 0) {
        throw new Error('Geen data gevonden in Excel bestand');
      }

      // Import data
      await onImport(mappedData);

      // Reset input
      e.target.value = '';
    } catch (error: any) {
      console.error('Error importing Excel:', error);
      toast({
        title: 'Fout bij importeren',
        description: error.message || 'Kon Excel bestand niet importeren',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="flex gap-2 mb-4">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleDownloadTemplate}
      >
        <Download className="w-4 h-4 mr-2" />
        Download Template
      </Button>
      
      <Button
        type="button"
        variant="default"
        size="sm"
        onClick={onZymplerClick}
      >
        <Upload className="w-4 h-4 mr-2" />
        Upload Zympler Export
      </Button>
    </div>
  );
}
